import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import SingleEvent from '../../../classes/events/SingleEvent';
import EventChat from './EventChat';
import './EventChatSection.css';

const MESSAGES_LIMIT = 30;

function EventChatSection({ eventId, isHost, isParticipant: isParticipantProp }) {
  const { t } = useTranslation(['events', 'shared']);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isParticipant, setIsParticipant] = useState(false);
  const currentUserIdRef = useRef(null);

  const handleNewMessage = useCallback(async (newMsg) => {
    if (newMsg.is_system) {
      setMessages((prev) => [...prev, { ...newMsg, users: null }]);
      return;
    }

    if (newMsg.user_id) {
      const { data: userData } = await supabase
        .from('users')
        .select('id, name, nickname, first_name, last_name')
        .eq('id', newMsg.user_id)
        .single();

      setMessages((prev) => [...prev, { ...newMsg, users: userData || null }]);
    } else {
      setMessages((prev) => [...prev, { ...newMsg, users: null }]);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const { data, hasMore: more, error } = await SingleEvent.loadMessages(eventId, MESSAGES_LIMIT);

      if (error) throw error;

      setMessages(data || []);
      setHasMore(more);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  }, [eventId]);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      setCurrentUser(user);
      currentUserIdRef.current = user?.id;

      if (user && !isParticipantProp) {
        const isUserParticipant = await SingleEvent.isParticipantInDB(eventId, user.id);
        setIsParticipant(isUserParticipant);
      } else {
        setIsParticipant(isParticipantProp || false);
      }
    });

    loadMessages();

    const messagesChannel = supabase
      .channel(`event_chat_${eventId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'event_messages',
          filter: `event_id=eq.${eventId}`,
        },
        (payload) => {
          handleNewMessage(payload.new);
        }
      )
      .subscribe();

    const participantsChannel = supabase
      .channel(`event_participants_${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_participants',
          filter: `event_id=eq.${eventId}`,
        },
        async () => {
          const userId = currentUserIdRef.current;
          if (userId) {
            const isUserParticipant = await SingleEvent.isParticipantInDB(eventId, userId);
            setIsParticipant(isUserParticipant);

            if (!isUserParticipant) {
              setMessages([]);
            }
          }
        }
      )
      .subscribe();

    return () => {
      messagesChannel.unsubscribe();
      participantsChannel.unsubscribe();
    };
  }, [eventId, isParticipantProp, loadMessages, handleNewMessage]);

  const loadOlderMessages = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;

    try {
      setLoadingMore(true);
      
      // Pobierz najstarszą wiadomość
      const oldestMessage = messages[0];
      
      const { data, hasMore: more, error } = await SingleEvent.loadMessages(
        eventId, 
        MESSAGES_LIMIT, 
        oldestMessage.id
      );

      if (error) throw error;

      if (data && data.length > 0) {
        // Dodaj starsze wiadomości na początek
        setMessages(prev => [...data, ...prev]);
      }
      setHasMore(more);
    } catch (err) {
      console.error('Error loading older messages:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSendMessage = async (messageText) => {
    if (!messageText.trim() || !currentUser) return;
    
    // Double check if user is still participant before sending
    if (!isHost && !isParticipant) {
      alert(t('events.chat.noAccess'));
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('event_messages')
        .insert({
          event_id: eventId,
          user_id: currentUser.id,
          message: messageText,
        });

      if (error) throw error;

      // Nowa wiadomość zostanie dodana przez realtime subscription (handleNewMessage)
      // Nie robimy pełnego reload, żeby nie resetować załadowanych starszych wiadomości
    } catch (err) {
      console.error('Error sending message:', err);
      alert(err.message || t('events.chat.error'));
    } finally {
      setLoading(false);
    }
  };

  // Check if user can access chat
  if (!isHost && !isParticipant) {
    return (
      <section className="event-chat-section">
        <div className="event-chat-section-restricted">
          <p>{t('events.details.joinToChat')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="event-chat-section">
      <h2 className="event-chat-section-title">{t('events.details.chat')}</h2>
      <EventChat
        messages={messages}
        currentUser={currentUser}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onSendMessage={handleSendMessage}
        onLoadMore={loadOlderMessages}
      />
    </section>
  );
}

export default EventChatSection;
